import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Globe2, Loader2, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@lightning/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@lightning/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { HowieCompanionSession, HowieConversation, supabase } from '../services/supabase';

type HistoryView = 'conversations' | 'companion';
type DeleteTarget = { type: HistoryView; id: string } | null;

function toPlainPreview(value: string) {
  return value
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_`>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatDateRelative(dateString: string) {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === now.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) return format(date, 'EEEE');
    if (now.getTime() - date.getTime() < 30 * 24 * 60 * 60 * 1000) return format(date, 'MMM d');
    return format(date, 'MMM d, yyyy');
  } catch {
    return 'Unknown';
  }
}

function getConversationPreview(conversation: HowieConversation) {
  if (conversation.last_message) return toPlainPreview(conversation.last_message);
  const lastUserMessage = [...conversation.messages].reverse().find((message) => message.role === 'user')?.content;
  return lastUserMessage ? toPlainPreview(lastUserMessage) : 'New conversation';
}

function getCompanionTitle(session: HowieCompanionSession) {
  return session.title || session.gmail_subject || session.meeting_code || 'Howie Companion';
}

function getCompanionPreview(session: HowieCompanionSession) {
  if (session.last_advice) return toPlainPreview(session.last_advice);
  if (session.surface === 'meet') return 'Live meeting coaching';
  if (session.surface === 'gmail') return 'Email coaching';
  return 'Browser coaching';
}

export default function HistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<HistoryView>('conversations');
  const [conversations, setConversations] = useState<HowieConversation[]>([]);
  const [companionSessions, setCompanionSessions] = useState<HowieCompanionSession[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingCompanion, setLoadingCompanion] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  useEffect(() => {
    if (!user) {
      setConversations([]);
      setCompanionSessions([]);
      setLoadingConversations(false);
      setLoadingCompanion(false);
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      setLoadingConversations(true);
      setLoadingCompanion(true);

      const [conversationResult, companionResult] = await Promise.all([
        supabase
          .from('howie_general_conversations')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('howie_companion_sessions')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false }),
      ]);

      if (cancelled) return;

      if (conversationResult.error) {
        console.error('Error fetching conversations:', conversationResult.error);
        toast.error('Failed to load conversation history');
      } else {
        setConversations((conversationResult.data || []) as HowieConversation[]);
      }

      if (companionResult.error) {
        console.error('Error fetching companion history:', companionResult.error);
        toast.error('Failed to load companion history');
      } else {
        setCompanionSessions((companionResult.data || []) as HowieCompanionSession[]);
      }

      setLoadingConversations(false);
      setLoadingCompanion(false);
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleDeleteConversation = async (conversationId: string) => {
    if (!user) {
      toast.error('Sign in to delete this conversation');
      setDeleteTarget(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('howie_general_conversations')
        .delete()
        .eq('id', conversationId)
        .eq('user_id', user.id);

      if (error) throw error;
      setConversations((current) => current.filter((conversation) => conversation.id !== conversationId));
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDeleteCompanionSession = async (sessionId: string) => {
    if (!user) {
      toast.error('Sign in to delete this companion session');
      setDeleteTarget(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('howie_companion_sessions')
        .delete()
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (error) throw error;
      setCompanionSessions((current) => current.filter((session) => session.id !== sessionId));
      toast.success('Companion session deleted');
    } catch (error) {
      console.error('Error deleting companion session:', error);
      toast.error('Failed to delete companion history');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'conversations') {
      handleDeleteConversation(deleteTarget.id);
    } else {
      handleDeleteCompanionSession(deleteTarget.id);
    }
  };

  const openConversation = (conversation: HowieConversation) => {
    navigate('/chat', {
      state: {
        conversationId: conversation.id,
        messages: conversation.messages,
      },
    });
  };

  const isLoading = activeView === 'conversations' ? loadingConversations : loadingCompanion;
  const deleteDialogTitle = deleteTarget?.type === 'companion'
    ? 'Delete companion session?'
    : 'Delete conversation?';
  const deleteDialogDescription = deleteTarget?.type === 'companion'
    ? 'This companion session will be permanently removed.'
    : 'This conversation will be permanently removed.';

  return (
    <div
      className="dojo-scroll h-full overflow-y-auto"
      style={{ background: 'linear-gradient(180deg, var(--how-paper-soft) 0%, var(--color-how-page-wash) 100%)' }}
    >
      <div className="mx-auto max-w-3xl px-5 py-8 sm:px-8 sm:py-10">
        <div className="text-center">
          <h1 className="font-body text-3xl font-medium leading-tight tracking-[-0.03em] text-how-space-deep sm:text-4xl">
            History
          </h1>
        </div>

        <Tabs value={activeView} onValueChange={(value) => setActiveView(value as HistoryView)} className="mt-8">
          {/* Compact on phones; wrapping remains available for larger text settings. */}
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
            <TabsList className="h-auto w-fit justify-start gap-0.5 rounded-xl bg-black/[0.045] p-1 sm:gap-1">
              <TabsTrigger
                value="conversations"
                className="h-9 rounded-lg px-2 font-body text-[13px] font-normal text-how-text-secondary shadow-none transition-all data-[state=active]:bg-white data-[state=active]:text-how-space-deep data-[state=active]:shadow-surface sm:px-4 sm:text-sm"
              >
                Recent
              </TabsTrigger>
              <TabsTrigger
                value="companion"
                className="h-9 rounded-lg px-2 font-body text-[13px] font-normal text-how-text-secondary shadow-none transition-all data-[state=active]:bg-white data-[state=active]:text-how-space-deep data-[state=active]:shadow-surface sm:px-4 sm:text-sm"
              >
                Companion
              </TabsTrigger>
            </TabsList>
            <button
              type="button"
              onClick={() => navigate('/chat', { state: { newConversation: true } })}
              className="inline-flex h-10 flex-none items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-how-space-deep px-3 text-[13px] text-white transition-colors hover:bg-how-space-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2 sm:px-4 sm:text-sm"
            >
              <Plus size={15} aria-hidden="true" className="hidden sm:block" />
              New chat
            </button>
          </div>

          <TabsContent value="conversations" className="mt-5 focus-visible:ring-how-space-deep">
            {isLoading ? (
              <div className="flex min-h-56 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-how-text-secondary/40" aria-label="Loading conversations" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex min-h-64 flex-col items-center justify-center text-center">
                <MessageSquare className="mb-3 h-8 w-8 text-how-text-secondary/20" aria-hidden="true" />
                <p className="text-sm text-how-space-deep">No conversations yet</p>
                <p className="mt-1 text-sm text-how-text-secondary/70">Your conversations with Howie will appear here.</p>
              </div>
            ) : (
              <div className="divide-y divide-black/5">
                {conversations.map((conversation) => (
                  <article key={conversation.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => openConversation(conversation)}
                      className="history-row-open grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-[18px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2 sm:grid-cols-[minmax(0,1fr)_6.5rem] sm:gap-6"
                    >
                      <span className="min-w-0 truncate whitespace-nowrap text-sm text-how-space-deep">
                        {getConversationPreview(conversation)}
                      </span>
                      <span className="history-row-date w-full whitespace-nowrap text-right text-xs tabular-nums text-gray-500 transition-opacity duration-150">
                        {formatDateRelative(conversation.updated_at)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget({ type: 'conversations', id: conversation.id })}
                      aria-label="Delete conversation"
                      className="history-row-delete absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-how-text-secondary/85 transition-[color,opacity] duration-150 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </article>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="companion" className="mt-5 focus-visible:ring-how-space-deep">
            {isLoading ? (
              <div className="flex min-h-56 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-how-text-secondary/40" aria-label="Loading companion sessions" />
              </div>
            ) : companionSessions.length === 0 ? (
              <div className="flex min-h-64 flex-col items-center justify-center text-center">
                <Globe2 className="mb-3 h-8 w-8 text-how-text-secondary/20" aria-hidden="true" />
                <p className="text-sm text-how-space-deep">No companion sessions yet</p>
                <p className="mt-1 text-sm text-how-text-secondary/70">Sessions from meetings, email, and the browser will appear here.</p>
              </div>
            ) : (
              <div className="divide-y divide-black/5">
                {companionSessions.map((session) => (
                  <article key={session.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => navigate(`/companion/${session.id}`)}
                      className="history-row-open grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-[18px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2 sm:grid-cols-[minmax(0,1fr)_6.5rem] sm:gap-6"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate whitespace-nowrap text-sm text-how-space-deep">{getCompanionTitle(session)}</span>
                        <span className="mt-1 block truncate text-xs text-how-text-secondary">{getCompanionPreview(session)}</span>
                      </span>
                      <span className="history-row-date w-full whitespace-nowrap text-right text-xs tabular-nums text-gray-500 transition-opacity duration-150">
                        {formatDateRelative(session.updated_at)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget({ type: 'companion', id: session.id })}
                      aria-label="Delete companion session"
                      className="history-row-delete absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-how-text-secondary/85 transition-[color,opacity] duration-150 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </article>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(isOpen) => !isOpen && setDeleteTarget(null)}>
        <AlertDialogContent className="max-w-md gap-0 rounded-2xl border border-how-space-deep/[0.07] bg-white p-6 shadow-popover [&>button:last-child]:right-2 [&>button:last-child]:top-2 [&>button:last-child]:h-11 [&>button:last-child]:w-11 sm:p-7 sm:[&>button:last-child]:right-6 sm:[&>button:last-child]:top-6 sm:[&>button:last-child]:h-8 sm:[&>button:last-child]:w-8">
          <AlertDialogHeader className="pt-9 text-left sm:pt-7">
            <AlertDialogTitle className="font-body text-xl font-medium leading-7 tracking-[-0.02em] text-how-space-deep">
              {deleteDialogTitle}
            </AlertDialogTitle>
            <AlertDialogDescription className="mt-2 font-body text-sm leading-6 text-how-text-secondary/80">
              {deleteDialogDescription} This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6 flex-row justify-end gap-2 space-y-0">
            <AlertDialogCancel className="h-10 min-w-24 rounded-full border-how-space-deep/10 bg-white px-4 font-body text-sm font-medium normal-case tracking-normal text-how-space-deep shadow-none hover:bg-how-rose/30 focus-visible:ring-2 focus-visible:ring-how-space-deep/60 focus-visible:ring-offset-2">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="h-10 min-w-24 rounded-full bg-red-600 px-4 font-body text-sm font-medium normal-case tracking-normal text-white shadow-none hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
